// File: src/pages/AdminPromoters.jsx

import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import AdminLoginWrapper from '../components/AdminLoginWrapper';

export default function AdminPromoters(props) {
  const [promoters, setPromoters] = useState([]);

  // Create form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Edit form state
  const [editing, setEditing] = useState(null); // { _id, name, email, imageUrl, hostSince }
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);

  useEffect(() => {
    fetchPromoters();
  }, []);

  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
  });

  const fetchPromoters = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/promoters`, {
      headers: authHeader(),
    });
    setPromoters(Array.isArray(res.data) ? res.data : []);
  };

  const uploadPromoterImage = async (file) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append('profilePic', file);
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/promoters/upload-image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data', ...authHeader() } }
    );
    return res.data.imageUrl;
  };

  const addPromoter = async () => {
  setUploading(true);

  // ✅ block blank email
  if (!email.trim()) {
    alert('Host email is required.');
    setUploading(false);
    return;
  }

  const normalizedEmail = email.trim().toLowerCase();
  const normalizedName = name.trim();

  try {
    const imageUrl = await uploadPromoterImage(imageFile);

    if (!imageUrl) {
      alert('Image upload failed');
      return;
    }

    await axios.post(
      `${process.env.REACT_APP_API_URL}/admin/promoters`,
      { name: normalizedName, imageUrl, email: normalizedEmail },
      { headers: authHeader() }
    );

    await fetchPromoters();

    setName('');
    setEmail('');
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    props.onPromoterUpdate?.();
  } catch (err) {
    const msg =
      err?.response?.status === 409
        ? 'That email is already assigned to another promoter.'
        : (err?.response?.data?.message || err.message);

    alert(msg);
  } finally {
    setUploading(false);
  }
};


  const deletePromoter = async (id) => {
    await axios.delete(`${process.env.REACT_APP_API_URL}/admin/promoters/${id}`, {
      headers: authHeader(),
    });
    await fetchPromoters();
    props.onPromoterUpdate?.();
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    setImageFile(file);
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };

  // --- Inline helpers (no shared utils) ---
  function fmtDate(iso) {
    if (!iso) return '(unknown)';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '(unknown)';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function tenureLabelFrom(dateLike) {
    if (!dateLike) return 'New host';
    const start = new Date(dateLike);
    if (Number.isNaN(start.getTime())) return 'New host';
    const now = new Date();
    let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    if (now.getDate() < start.getDate()) months -= 1;
    if (months <= 0) return 'New host';
    if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'}`;
    const years = Math.floor(months / 12);
    return `${years} ${years === 1 ? 'year' : 'years'}`;
  }

  function toDateInputValue(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    // yyyy-MM-dd
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  function fromDateInputValue(val) {
    // val is yyyy-MM-dd or empty
    if (!val) return null;
    // Interpret as local date at midnight
    const [y, m, d] = val.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d).toISOString(); // send ISO back
  }

  // --- Edit flow ---
  const openEdit = (p) => {
    setEditing({
      _id: p._id,
      name: p.name || '',
      email: p.email || '',
      imageUrl: p.imageUrl || '',
      hostSince: p.hostSince || p.createdAt || null,
    });
    setEditImageFile(null);
    setEditImagePreview(null);
  };

  const handleEditImage = (e) => {
    const file = e.target.files?.[0] || null;
    setEditImageFile(file);
    setEditImagePreview(file ? URL.createObjectURL(file) : null);
  };

  const saveEdit = async () => {
    if (!editing?._id) return;
    let imageUrl = editing.imageUrl;

    // If user chose a new image, upload it
    if (editImageFile) {
      const uploaded = await uploadPromoterImage(editImageFile);
      if (!uploaded) {
        alert('Image upload failed');
        return;
      }
      imageUrl = uploaded;
    }

    if (!editing.email?.trim()) {
  alert('Host email is required.');
  return;
}

    const payload = {
      name: editing.name?.trim() || '',
      email: editing.email?.trim().toLowerCase() || '',
      imageUrl,
      hostSince: editing.hostSince ? new Date(editing.hostSince).toISOString() : null,
    };

    // Assumes you have PUT or PATCH route for updating a promoter by id
    // e.g. PUT /admin/promoters/:id
    await axios.put(
      `${process.env.REACT_APP_API_URL}/admin/promoters/${editing._id}`,
      payload,
      { headers: authHeader() }
    );

    setEditing(null);
    setEditImageFile(null);
    setEditImagePreview(null);

    await fetchPromoters();
    props.onPromoterUpdate?.();
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditImageFile(null);
    setEditImagePreview(null);
  };

  return (
    <AdminLoginWrapper>
      <div style={{ margin: 20 }}>
        <h2>Manage Promoters</h2>

        {/* Create form */}
        <div style={{ display: 'grid', gap: 8, maxWidth: 360, marginBottom: 16 }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Host Name"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Host Email"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          {imagePreview && (
            <div>
              <img
                src={imagePreview}
                alt="Preview"
                style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
              />
            </div>
          )}
          <button onClick={addPromoter} disabled={uploading}>
            {uploading ? 'Uploading...' : 'Add Host'}
          </button>
        </div>

        {/* List */}
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {promoters.map((p) => {
            const basis = p.hostSince || p.createdAt || null;
            const tenure = tenureLabelFrom(basis);
            return (
              <li
                key={p._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: '1px solid #eee',
                }}
              >
                <img
                  src={p.imageUrl}
                  alt={p.name}
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    background: '#f3f4f6',
                  }}
                />
                <div style={{ flexGrow: 1 }}>
                  <div style={{ fontWeight: 700 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {p.email ? <a href={`mailto:${p.email}`}>{p.email}</a> : '(no email)'}
                  </div>
                  <div style={{ fontSize: 12, color: '#444', marginTop: 2 }}>
                    Hosted since: <strong>{fmtDate(basis)}</strong> &nbsp;({tenure})
                  </div>
                  {(() => {
  const s = String(p.docsStatus || 'unknown');

  const pillBase = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 900,
    border: '1px solid #e5e7eb',
    marginLeft: 6,
    whiteSpace: 'nowrap',
  };

  const pill =
    s === 'accepted'
      ? { ...pillBase, background: '#ecfdf5', color: '#065f46' }
      : s === 'outdated'
      ? { ...pillBase, background: '#fffbeb', color: '#92400e' }
      : s === 'not_accepted'
      ? { ...pillBase, background: '#fef2f2', color: '#991b1b' }
      : { ...pillBase, background: '#f8fafc', color: '#111' };

  const label =
    s === 'accepted' ? 'Docs accepted' :
    s === 'outdated' ? 'Docs outdated' :
    s === 'not_accepted' ? 'Docs NOT accepted' :
    s === 'no_user' ? 'No user account' :
    s === 'no_email' ? 'No email' :
    'Docs unknown';

  const meta =
    p.docsAcceptedAt ? ` • ${fmtDate(p.docsAcceptedAt)}` : '';

  return (
    <div style={{ fontSize: 12, color: '#444', marginTop: 6 }}>
      Docs:
      <span style={pill} title={`Accepted: ${p.docsVersion || '—'} • Required: ${p.docsRequiredVersion || '—'}${p.docsSignatureName ? ` • Signed: ${p.docsSignatureName}` : ''}`}>
        {label}{meta}
      </span>
    </div>
  );
})()}

                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => openEdit(p)}>Edit</button>
                  <button onClick={() => deletePromoter(p._id)} style={{ color: '#b91c1c' }}>
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Edit drawer/modal (simple inline panel) */}
        {editing && (
          <div
            style={{
              marginTop: 20,
              padding: 16,
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              maxWidth: 520,
              background: '#fff',
            }}
          >
            <h3 style={{ marginTop: 0 }}>Edit Promoter</h3>
            <div style={{ display: 'grid', gap: 8 }}>
              <label style={{ fontSize: 12, color: '#666' }}>Name</label>
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="Host Name"
              />

              <label style={{ fontSize: 12, color: '#666' }}>Email</label>
              <input
                type="email"
                value={editing.email}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                placeholder="Host Email"
              />

              <label style={{ fontSize: 12, color: '#666' }}>Hosted Since</label>
              <input
                type="date"
                value={toDateInputValue(editing.hostSince)}
                onChange={(e) =>
                  setEditing({ ...editing, hostSince: fromDateInputValue(e.target.value) })
                }
              />

              <label style={{ fontSize: 12, color: '#666' }}>Image</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img
                  src={editImagePreview || editing.imageUrl}
                  alt="Promoter"
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    background: '#f3f4f6',
                  }}
                />
                <input type="file" accept="image/*" onChange={handleEditImage} />
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={saveEdit}>Save</button>
                <button onClick={cancelEdit}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLoginWrapper>
  );
}
